import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../config/URL";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {
  ThemeProvider,
  createTheme,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/material";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import GlobalDelete from "../../components/common/GlobalDelete";
import fetchAllCentersWithIds from "../../pages/List/CenterList";
import { exportVisibleColumnsToCSV } from "../../components/common/ExportCsv";
import {
  getColumnPreference,
  saveColumnPreference,
} from "../../utility/mrtColumnPersistence";

const MODULE_KEY = "STAFFATTENDANCE";

const StaffingAttendance = ({ selectedCenter }) => {
  const [filters, setFilters] = useState({
    centerId: selectedCenter,
  });

  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [centerData, setCenterData] = useState(null);
  /* -------------------- ROLE MATRIX -------------------- */
  const storedScreens = JSON.parse(localStorage.getItem("screens") || "{}");

  const canIndex = Boolean(storedScreens?.staffAttendanceIndex);
  const canRead = Boolean(storedScreens?.staffAttendanceRead);
  const canCreate = Boolean(storedScreens?.staffAttendanceCreate);
  const canUpdate = Boolean(storedScreens?.staffAttendanceUpdate);
  const canDelete = Boolean(storedScreens?.staffAttendanceDelete);

  const showActionColumn = canUpdate || canDelete;
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const defaultColumnVisibility = {
    attendanceRemark: false,
    modeOfWorking: false,
    userId: false,
    userRole: false,
    createdAt: false,
    updatedAt: false,
  };

  const [columnVisibility, setColumnVisibility] = useState(() =>
    getColumnPreference(MODULE_KEY, defaultColumnVisibility),
  );

  /* -------------------- FETCH CENTER -------------------- */
  const fetchData = async () => {
    try {
      const centerData = await fetchAllCentersWithIds();
      setCenterData(centerData);
    } catch (error) {
      toast.error(error);
    }
  };

  /* -------------------- COLUMNS -------------------- */
  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorFn: (row, index) => index + 1,
        header: "S.NO",
        size: 20,
        Cell: ({ cell }) => (
          <span style={{ textAlign: "center" }}>{cell.getValue()}</span>
        ),
      },
      {
        accessorKey: "attendanceStatus",
        header: "Status",
        size: 30,
        Cell: ({ row }) =>
          row.original.attendanceStatus === "Present" ||
          row.original.attendanceStatus === "PRESENT" ? (
            <span className="badge badges-Green fw-light">Present</span>
          ) : row.original.attendanceStatus === "Absent" ||
            row.original.attendanceStatus === "ABSENT" ? (
            <span className="badge badges-orange fw-light">Absent</span>
          ) : null,
      },
      // { accessorKey: "centerName", enableHiding: true, header: "Centre Name" },
      { accessorKey: "employeeName", header: "Employee Name" },
      {
        accessorKey: "checkIn",
        size: 30,
        enableHiding: true,
        header: "Check In",
        Cell: ({ cell }) => {
          const timeValue = cell.getValue();
          if (timeValue) {
            const [hours, minutes] = timeValue.split(":");
            const date = new Date();
            date.setHours(hours, minutes);
            const options = {
              hour: "numeric",
              minute: "numeric",
              hour12: true,
            };
            return date.toLocaleTimeString(undefined, options);
          }
          return null;
        },
      },
      {
        accessorKey: "checkOut",
        size: 30,
        enableHiding: true,
        header: "Check Out",
        Cell: ({ cell }) => {
          const timeValue = cell.getValue();
          if (timeValue) {
            const [hours, minutes] = timeValue.split(":");
            const date = new Date();
            date.setHours(hours, minutes);
            const options = {
              hour: "numeric",
              minute: "numeric",
              hour12: true,
            };
            return date.toLocaleTimeString(undefined, options);
          }
          return null;
        },
      },
      { accessorKey: "date", enableHiding: true, header: "Date" },
      { accessorKey: "createdBy", enableHiding: true, header: "Created By" },
      {
        accessorKey: "updatedBy",
        header: "Updated By",
        enableHiding: true,
        Cell: ({ cell }) => cell.getValue() || "",
      },
      { accessorKey: "attendanceRemark", header: "Attendance Remark" },
      {
        accessorKey: "modeOfWorking",
        header: "Mode Of Working",
        Cell: ({ row }) =>
          row.original.modeOfWorking === "WORK_FROM_HOME" ||
          row.original.modeOfWorking === "work_from_home" ? (
            <span>WORK FROM HOME</span>
          ) : row.original.modeOfWorking === "WORK_FROM_OFFICE" ||
            row.original.modeOfWorking === "work_from_office" ? (
            <span>WORK FROM OFFICE</span>
          ) : null,
      },
      { accessorKey: "userId", header: "User Id" },
      {
        accessorKey: "userRole",
        header: "User Role",
        Cell: ({ row }) =>
          row.original.userRole === "SMS_TEACHER" ||
          row.original.userRole === "sms_teacher" ? (
            <span>SMS TEACHER</span>
          ) : row.original.userRole === "CENTER_MANAGER" ||
            row.original.userRole === "center_manager" ? (
            <span>CENTER MANAGER</span>
          ) : null,
      },
      { accessorKey: "otEndTime", header: "Ot End Time" },
      { accessorKey: "otStartTime", header: "Ot Start Time" },
      {
        accessorKey: "createdAt",
        header: "Created On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      },
      {
        accessorKey: "updatedAt",
        header: "Updated On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10) || "",
      },
    ];

    if (!showActionColumn) return baseColumns;

    return [
      baseColumns[0], // S.NO
      {
        accessorKey: "id",
        header: "",
        size: 20,
        enableSorting: false,
        Cell: ({ cell }) => (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
              setSelectedId(cell.getValue());
            }}
          >
            <MoreVertIcon />
          </IconButton>
        ),
      },
      ...baseColumns.slice(1),
    ];
  }, [showActionColumn]);

  /* -------------------- FETCH DATA -------------------- */
  const getData = async () => {
    try {
      setLoading(true);
      const url =
        selectedCenter === 0 || selectedCenter === "0"
          ? `getAllUserAttendances`
          : `/getAllUserAttendances?centerId=${selectedCenter}`;

      const response = await api.get(url);
      setData(response.data);
    } catch (error) {
      console.error("Error fetching data ", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [selectedCenter]);
  // const getData = async () => {
  //   try {
  //     setLoading(true);
  //     const filteredFilters = Object.fromEntries(
  //       Object.entries(filters).filter(
  //         ([key, value]) =>
  //           value !== "" && value !== null && value !== undefined
  //       )
  //     );
  //     const queryParams = new URLSearchParams(filteredFilters).toString();
  //     const response = await api.get(`/getAllUserAttendances?${queryParams}`);
  //     setData(response.data);
  //   } catch (error) {
  //     console.error("Error fetching data:", error);
  //   } finally {
  //     setLoading(false);
  //   }
  //   // try {
  //   //   const response = await api.get("/getAllUserAttendances");
  //   //   setData(response.data);
  //   //   setLoading(false);
  //   // } catch (error) {
  //   //   toast.error("Error Fetching Data : ", error);
  //   // }
  // };
  useEffect(() => {
    setFilters((prev) => ({ ...prev, centerId: selectedCenter }));
  }, [selectedCenter]);
  useEffect(() => {
    getData();
  }, [filters]);
  // useEffect(() => {
  //   getData();
  //   fetchData();
  // }, []);
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  const clearFilter = () => {
    setFilters({
      centerId: selectedCenter,
    });
  };

  /* -------------------- THEME -------------------- */
  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
    },
  });

  const handleMenuClose = () => setMenuAnchor(null);

  /* -------------------- TABLE INSTANCE -------------------- */
  const table = useMaterialReactTable({
    columns,
    data,
    state: {
      columnVisibility,
    },
    onColumnVisibilityChange: (updater) => {
      const newVisibility =
        typeof updater === "function" ? updater(columnVisibility) : updater;

      setColumnVisibility(newVisibility);
      saveColumnPreference(MODULE_KEY, newVisibility);
    },
    enableColumnActions: true,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <button
        className="btn btn-sm btn-border"
        onClick={() =>
          exportVisibleColumnsToCSV(table, data, "StaffAttendanceList.csv")
        }
      >
        Export CSV
      </button>
    ),
    muiTableBodyRowProps: canRead
      ? ({ row }) => ({
          onClick: () =>
            navigate(`/staffing/attendance/view/${row.original.id}`),
          style: { cursor: "pointer" },
        })
      : undefined,
  });
  return (
    <div className="container-fluid center my-4">
      <ol
        className="breadcrumb my-3"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          &nbsp;Staffing
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="active breadcrumb-item" aria-current="page">
          &nbsp;Attendance
        </li>
      </ol>
      <div className="card">
        <div
          className="d-flex align-items-center justify-content-between p-1 mb-3"
          style={{ background: "#f5f7f9" }}
        >
          <div className="d-flex align-items-center">
            <div className="d-flex">
              <div className="active dot"></div>
            </div>
            <span className="text-muted me-2">
              This database shows the list of{" "}
              <span className="bold" style={{ color: "#287f71" }}>
                Attendance
              </span>
            </span>
            <input type="hidden" name="centerId" value={filters.centerId} />
          </div>
        </div>
        <div className="d-flex justify-content-end mb-3">
          {canCreate && (
            <Link to="/staffing/attendance/add">
              <button
                type="button"
                className="btn btn-button btn-sm me-2"
                style={{ fontWeight: "600px !important" }}
              >
                &nbsp; Add &nbsp;&nbsp; <i className="bx bx-plus"></i>
              </button>
            </Link>
          )}
        </div>
        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <>
            <ThemeProvider theme={theme}>
              <MaterialReactTable table={table} />
            </ThemeProvider>
            {showActionColumn && (
              <Menu
                id="action-menu"
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
              >
                {canUpdate && (
                  <MenuItem
                    onClick={() =>
                      navigate(`/staffing/attendance/edit/${selectedId}`)
                    }
                    className="text-start mb-0 menuitem-style"
                  >
                    Edit
                  </MenuItem>
                )}
                {canDelete && (
                  <MenuItem>
                    <GlobalDelete
                      path={`/deleteUserAttendance/${selectedId}`}
                      onDeleteSuccess={getData}
                      onOpen={handleMenuClose}
                    />
                  </MenuItem>
                )}
              </Menu>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StaffingAttendance;
